import React, { useRef, useState } from 'react';
import Typography from 'new/elements/TypographySupport';
import { Popover, List, ListItemButton, ListItemText, Tooltip } from '@mui/material';
import { ReactComponent as PenIcon } from 'new/components/icons/fi-rr-pencil.svg';
import { ReactComponent as EllipsisIcon } from 'new/components/icons/Ellipsis.svg';
import { TreeNode } from '../BreadcrumbTreeView/types';
import classes from './Breadcrumbs.module.scss';
import { ReactComponent as FolderIcon } from 'new/components/icons/fi-rr-folder.svg';
import { Home as HomeIcon } from '@mui/icons-material';

interface BreadcrumbsProps {
  path: TreeNode[];
  onBreadcrumbClick: (index: number) => void;
  onEditClick?: () => void;
  showEditIcon?: boolean;
}

const MAX_VISIBLE_BREADCRUMBS = 4;

type BreadcrumbTextWithTooltipProps = {
  text: string;
  color: any;
  className?: string;
  onClick: () => void;
};

const BreadcrumbTextWithTooltip: React.FC<BreadcrumbTextWithTooltipProps> = ({
  text,
  color,
  className,
  onClick,
}) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  React.useEffect(() => {
    const el = textRef.current;
    if (el) {
      const truncated = el.scrollWidth > el.clientWidth;
      if (truncated !== isTruncated) setIsTruncated(truncated);
    }
    const handle = () => {
      const current = textRef.current;
      if (!current) return;
      const truncatedNow = current.scrollWidth > current.clientWidth;
      if (truncatedNow !== isTruncated) setIsTruncated(truncatedNow);
    };
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, [text, isTruncated]);

  const content = (
    <Typography
      variant="body2"
      color={color}
      className={className}
      onClick={onClick}
      component="span"
      ref={textRef as any}
    >
      {text}
    </Typography>
  );

  return (
    <Tooltip title={text} disableHoverListener={!isTruncated}>
      <span style={{ display: 'inline-block', maxWidth: '100%' }}>{content}</span>
    </Tooltip>
  );
};

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  path,
  onBreadcrumbClick,
  onEditClick,
  showEditIcon = true,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleBreadcrumbItemClick = (index: number) => {
    onBreadcrumbClick(index);
    handlePopoverClose();
  };

  if (!path.length) {
    return (
      <div className={classes.breadcrumbsListContainer}>
        <div className={classes.breadcrumb}>
          <HomeIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
        </div>
        {onEditClick && showEditIcon && (
          <div
            className={classes.editIconContainer}
            onClick={onEditClick}
            data-testid="edit-path-button"
          >
            <PenIcon />
          </div>
        )}
      </div>
    );
  }

  const isTruncated = path.length > MAX_VISIBLE_BREADCRUMBS;
  const numVisibleEndItems = 2;
  const visibleItems = isTruncated ? path.slice(path.length - numVisibleEndItems) : path;
  const hiddenItems = isTruncated
    ? [{ id: 'main', name: 'Main' }, ...path.slice(0, path.length - numVisibleEndItems)]
    : [];

  return (
    <div className={classes.breadcrumbs}>
      <div className={classes.breadcrumbsListContainer}>
        {!isTruncated && (
          <div onClick={() => onBreadcrumbClick(-1)} className={classes.breadcrumb}>
            <HomeIcon fontSize="small" style={{ verticalAlign: 'middle' }} />
          </div>
        )}

        {isTruncated && (
          <>
            <div
              onClick={handlePopoverOpen}
              ref={anchorRef}
              className={classes.ellipsisIconContainer}
            >
              <EllipsisIcon />
            </div>
            <Popover
              open={isPopoverOpen}
              anchorEl={anchorRef.current}
              onClose={handlePopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              className={classes.popover}
            >
              <List dense className={classes.popoverList}>
                {hiddenItems.map((node, index) => (
                  <ListItemButton
                    key={node.id}
                    onClick={() => {
                      if (node.id === 'main') {
                        onBreadcrumbClick(-1);
                      } else {
                        const actualIndex = index - 1;
                        handleBreadcrumbItemClick(actualIndex);
                      }
                      handlePopoverClose();
                    }}
                    className={classes.popoverItem}
                  >
                    {Array.from({ length: index }, (_, i) => (
                      <div key={i} className={classes.levelIndicator} />
                    ))}
                    {node.id === 'main' ? (
                      <>
                        <HomeIcon fontSize="small" />
                        <ListItemText primary="Home" className={classes.breadcrumb} />
                      </>
                    ) : (
                      <>
                        <FolderIcon />
                        <ListItemText
                          primary={
                            ((node as any).displayName ||
                              (node as any).display_name ||
                              node.name) as string
                          }
                          className={classes.breadcrumb}
                        />
                      </>
                    )}
                  </ListItemButton>
                ))}
              </List>
            </Popover>
          </>
        )}

        {visibleItems.map((node) => {
          const originalIndex = path.indexOf(node);
          return (
            <React.Fragment key={node.id}>
              <div className={classes.breadcrumbSeparator}>/</div>

              <BreadcrumbTextWithTooltip
                text={(node.displayName || (node as any).display_name || node.name) as string}
                color={originalIndex === path.length - 1 ? 'textPrimary' : 'textSecondary'}
                className={classes.breadcrumb}
                onClick={() => handleBreadcrumbItemClick(originalIndex)}
              />
            </React.Fragment>
          );
        })}

        {onEditClick && showEditIcon && (
          <div
            className={classes.editIconContainer}
            onClick={onEditClick}
            data-testid="edit-path-button"
          >
            <PenIcon />
          </div>
        )}
      </div>
    </div>
  );
};

export default Breadcrumbs;
